import { useEffect, useRef, useState, useCallback } from 'react';
import { useFrame } from '@react-three/fiber';
import { useAudio } from '../hooks/useAudio';
import useAppStore from '../stores/appStore';

// Lazy-load Tone.js to avoid AudioContext creation on import
let ToneModule = null;
const getTone = async () => {
  if (!ToneModule) {
    ToneModule = await import('tone');
  }
  return ToneModule;
};

/**
 * AudioManager - Manages audio playback and analysis within the Canvas
 * This component must be inside the Canvas to use useFrame
 *
 * CRITICAL: Uses refs to track state changes to avoid infinite re-render loops.
 * The audio object from useAudio is memoized, but we still use refs for guards.
 */
function AudioManager() {
  const {
    audioPlaying,
    audioVolume,
    currentChapter,
    hasAudioInteraction,
    setHasAudioInteraction,
    setAudioData
  } = useAppStore();

  // Initialize audio system
  const audio = useAudio('/audio/');

  // Analysis nodes
  const analyzerRef = useRef(null);
  const meterRef = useRef(null);
  const frameCountRef = useRef(0);
  const [analyzersReady, setAnalyzersReady] = useState(false);
  const THROTTLE_FRAMES = 3; // Update every 3 frames (~20fps instead of 60fps)

  // Refs to track previous values and prevent duplicate calls
  const prevAudioPlayingRef = useRef(audioPlaying);
  const prevChapterRef = useRef(currentChapter);
  const prevVolumeRef = useRef(audioVolume);
  const prevHasInteractionRef = useRef(hasAudioInteraction);
  const isProcessingRef = useRef(false); // Prevents concurrent operations
  const analyzersInitializedRef = useRef(false);

  // Initialize analyzers only once after first audio interaction
  useEffect(() => {
    // Guard: Only initialize once
    if (analyzersInitializedRef.current || !hasAudioInteraction) {
      return;
    }

    const initAnalyzers = async () => {
      try {
        const currentPlayer = audio.getCurrentPlayer();

        // Guard: Need a player to connect to
        if (!currentPlayer) {
          return;
        }

        // Get the lazy-loaded Tone module
        const Tone = await getTone();

        // Create FFT analyzer
        analyzerRef.current = new Tone.FFT(128);
        meterRef.current = new Tone.Meter({ normalRange: true, smoothing: 0.8 });

        // Connect to player
        currentPlayer.connect(analyzerRef.current);
        currentPlayer.connect(meterRef.current);

        analyzersInitializedRef.current = true;
        setAnalyzersReady(true);
        console.log('Audio analyzers connected');
      } catch (err) {
        console.error('Failed to create analyzers:', err);
      }
    };

    initAnalyzers();
  }, [hasAudioInteraction, audio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (analyzerRef.current) {
        analyzerRef.current.dispose();
        analyzerRef.current = null;
      }
      if (meterRef.current) {
        meterRef.current.dispose();
        meterRef.current = null;
      }
      analyzersInitializedRef.current = false;
    };
  }, []);

  // Handle play/pause state changes
  useEffect(() => {
    // Guard: Skip if processing or no actual change
    if (isProcessingRef.current) return;
    if (prevAudioPlayingRef.current === audioPlaying &&
        prevHasInteractionRef.current === hasAudioInteraction) {
      return;
    }

    const chapterToPlay = currentChapter !== null ? currentChapter : 0;

    const handlePlaybackChange = async () => {
      isProcessingRef.current = true;

      try {
        // First play ever: mark interaction and start
        if (audioPlaying && !prevHasInteractionRef.current && !hasAudioInteraction) {
          setHasAudioInteraction();
          await audio.play(chapterToPlay);
        }
        // Play requested (and we have interaction)
        else if (audioPlaying && !prevAudioPlayingRef.current) {
          await audio.play(chapterToPlay);
        }
        // Pause requested
        else if (!audioPlaying && prevAudioPlayingRef.current) {
          audio.pause();
        }
      } catch (err) {
        console.error('Playback error:', err);
      } finally {
        isProcessingRef.current = false;
        prevAudioPlayingRef.current = audioPlaying;
        prevHasInteractionRef.current = hasAudioInteraction;
      }
    };

    handlePlaybackChange();
  }, [audioPlaying, hasAudioInteraction, currentChapter, audio, setHasAudioInteraction]);

  // Handle volume changes
  useEffect(() => {
    if (prevVolumeRef.current !== audioVolume) {
      audio.setVolume(audioVolume);
      prevVolumeRef.current = audioVolume;
    }
  }, [audioVolume, audio]);

  // Handle chapter changes - crossfade or start playing when chapter changes
  useEffect(() => {
    // Guard: Skip if chapter didn't actually change
    if (prevChapterRef.current === currentChapter) {
      return;
    }

    const handleChapterChange = async () => {
      if (currentChapter !== null && hasAudioInteraction && audioPlaying) {
        // Check if something is currently playing to crossfade from
        const currentPlayer = audio.getCurrentPlayer();
        if (currentPlayer && currentPlayer.state === 'started') {
          // Crossfade from current track to new chapter's track
          audio.crossfadeToChapter(currentChapter, 2);
        } else {
          // Nothing playing yet - just start playing the new chapter directly
          await audio.play(currentChapter);
        }
      }
    };

    handleChapterChange();
    prevChapterRef.current = currentChapter;
  }, [currentChapter, hasAudioInteraction, audioPlaying, audio]);

  // Update FFT data every N frames (throttled for performance)
  useFrame(() => {
    if (!analyzerRef.current || !analyzersReady) return;

    frameCountRef.current++;

    // Only update every THROTTLE_FRAMES frames
    if (frameCountRef.current >= THROTTLE_FRAMES) {
      frameCountRef.current = 0;

      try {
        const fftData = analyzerRef.current.getValue();

        // Convert from dB to linear (0 to 1), clamping to valid range
        const normalizedFFT = fftData.map(db => {
          const clamped = Math.min(0, Math.max(-100, isFinite(db) ? db : -100));
          return (clamped + 100) / 100;
        });

        setAudioData(normalizedFFT);
      } catch (err) {
        // Silently fail - FFT might not be ready yet
      }
    }
  });

  return null; // This component doesn't render anything
}

export default AudioManager;
